import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
  theme: Theme;
  resolvedTheme: 'light' | 'dark'; // The actual theme being used
}

const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

const getInitialTheme = (): Theme => {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('theme') as Theme;
    if (saved && ['light', 'dark', 'system'].includes(saved)) {
      return saved;
    }
  }
  return 'system';
};

const getResolvedTheme = (theme: Theme): 'light' | 'dark' => {
  if (theme === 'system') {
    return getSystemTheme();
  }
  return theme;
};

const initialState: ThemeState = {
  theme: getInitialTheme(),
  resolvedTheme: getResolvedTheme(getInitialTheme()),
};

const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    setTheme: (state, action: PayloadAction<Theme>) => {
      state.theme = action.payload;
      state.resolvedTheme = getResolvedTheme(action.payload);
      
      // Save to localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('theme', action.payload);
        
        // Apply theme class to document
        const root = document.documentElement;
        const resolved = getResolvedTheme(action.payload);
        root.classList.remove('theme-light', 'theme-dark');
        root.classList.add(`theme-${resolved}`);
      }
    },
    updateResolvedTheme: (state) => {
      // Called when system theme changes
      if (state.theme === 'system') {
        state.resolvedTheme = getSystemTheme();
        
        if (typeof window !== 'undefined') {
          const root = document.documentElement;
          root.classList.remove('theme-light', 'theme-dark');
          root.classList.add(`theme-${state.resolvedTheme}`);
        }
      }
    },
  },
});

export const { setTheme, updateResolvedTheme } = themeSlice.actions;
export default themeSlice.reducer;

